import React, { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import TaskForm from './TaskForm';
import TaskCard from './TaskCard';
import AssigneeFilter from './AssigneeFilter';
import ActivityHistory from './ActivityHistory';
import useEscapeKey from '../utils/useEscapeKey';
import { generateLocalTimestamp } from '../utils/taskUtils';
import '../styles/ActivityHistory.css';

/** Evita crash si una etiqueta viene como objeto/número/null desde la API o DynamoDB */
function safeTagString(tag) {
  if (tag == null) return '';
  return String(tag).trim();
}

function normalizeTagList(rawTags) {
  if (!Array.isArray(rawTags)) return [];
  const out = [];
  const seen = new Set();
  for (const t of rawTags) {
    const normalized = safeTagString(t).replace(/,/g, '').toUpperCase();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  }
  return out;
}

function KanbanBoard({ showTaskForm, setShowTaskForm, onAddTask }) {
  const { 
    workspaces, 
    activeWorkspace, 
    updateTaskStatus, 
    updateTask, 
    addTask, 
    deleteTask, 
    updateWorkspaceFilter,
    updateTitleFilter,
    getUniqueTags,
    loadWorkspaceTagsFromDynamoDB,
    canUserDoOnBoard
  } = useKanban();
  const { currentUser } = useAuth();
  const [organizedTasks, setOrganizedTasks] = useState({
    todo: [],
    inProgress: [],
    done: []
  });
  
  // Estado para el popup de confirmación
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [taskToConfirm, setTaskToConfirm] = useState(null);
  const [evidenceComment, setEvidenceComment] = useState('');
  
  // Estado local para el filtro de etiquetas (se sincroniza con el workspace)
  const [availableTags, setAvailableTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Estado local para el filtro de títulos
  const [titleQuery, setTitleQuery] = useState('');
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const [filteredTitleSuggestions, setFilteredTitleSuggestions] = useState([]);
  const titleInputRef = useRef(null);
  const titleSuggestionsRef = useRef(null);
  const [availableTitles, setAvailableTitles] = useState([]);
  
  // Estado para el modal de historial de actividades
  const [showActivityHistory, setShowActivityHistory] = useState(false);

  // Organizar las tareas por status cada vez que cambia el workspace activo o sus tareas
  useEffect(() => {
    if (!workspaces[activeWorkspace]) return;
    
    const currentWorkspaceTasks = workspaces[activeWorkspace]?.tasks || [];
    const currentTagFilter = workspaces[activeWorkspace]?.activeFilter || '';
    const currentAssigneeFilter = workspaces[activeWorkspace]?.activeAssigneeFilter || '';
    const currentTitleFilter = workspaces[activeWorkspace]?.activeTitleFilter || '';
    
    // Aplicar filtro de etiquetas y responsable
    let filteredTasks = currentWorkspaceTasks;
    
    // Aplicar filtro de etiquetas si está activo
    if (currentTagFilter) {
      const filterLower = currentTagFilter.toLowerCase();
      filteredTasks = filteredTasks.filter(task => {
        if (!task.tags || !Array.isArray(task.tags)) return false;
        return task.tags.some(tag => {
          const s = safeTagString(tag);
          return s && s.toLowerCase().includes(filterLower);
        });
      });
    }
    
    // Aplicar filtro de responsable si está activo
    if (currentAssigneeFilter) {
      filteredTasks = filteredTasks.filter(task => 
        task.assignee === currentAssigneeFilter
      );
    }
    
    // Aplicar filtro de título si está activo
    if (currentTitleFilter) {
      filteredTasks = filteredTasks.filter(task => 
        task.title && task.title.toLowerCase().includes(currentTitleFilter.toLowerCase())
      );
    }
    
    // Función para ordenar tareas por fecha de creación (la más nueva primero)
    const sortByCreationDate = (tasks) => {
      return [...tasks].sort((a, b) => {
        // Función helper para parsear fechas de manera robusta
        const parseTimestamp = (timestamp) => {
          if (!timestamp) return new Date(0);
          
          try {
            // Formato estándar "HH:MM DD-MM-YYYY" (con 1 espacio)
            const standardFormat = /^\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/;
            if (typeof timestamp === 'string' && standardFormat.test(timestamp)) {
              const [timeStr, dateStr] = timestamp.split(' ');
              const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
              const [day, month, year] = dateStr.split('-').map(num => parseInt(num, 10));
              return new Date(year, month - 1, day, hours, minutes, 0);
            }
            
            // Formato legacy con múltiples espacios
            if (typeof timestamp === 'string' && timestamp.includes('     ')) {
              const parts = timestamp.split('     ');
              if (parts.length === 2) {
                const [timeStr, dateStr] = parts;
                const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
                const dateParts = dateStr.split(/[/-]/).map(num => parseInt(num, 10));
                if (dateParts.length === 3) {
                  // Asumimos formato DD/MM/YYYY o DD-MM-YYYY
                  return new Date(dateParts[2], dateParts[1] - 1, dateParts[0], hours, minutes, 0);
                }
              }
            }
            
            // Fallback: intentar parsear como fecha normal
            const fallbackDate = new Date(timestamp);
            return isNaN(fallbackDate.getTime()) ? new Date(0) : fallbackDate;
          } catch (e) {
            console.warn('Error parsing timestamp in KanbanBoard:', timestamp, e);
            return new Date(0);
          }
        };
        
        const dateA = parseTimestamp(a.createdTimestamp);
        const dateB = parseTimestamp(b.createdTimestamp);
        
        // Usar .getTime() para comparación consistente
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();
        
        // Si alguna fecha es inválida (timestamp = 0), colocarla al final
        if (timeA === 0 && timeB === 0) return 0;
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;
        
        // Ordenar de más reciente a más antigua
        return timeB - timeA;
      });
    };
    
    const tasksByStatus = {
      todo: sortByCreationDate(filteredTasks.filter(task => task.status === 'todo')),
      inProgress: sortByCreationDate(filteredTasks.filter(task => task.status === 'inProgress')),
      done: sortByCreationDate(filteredTasks.filter(task => task.status === 'done'))
    };
    
    setOrganizedTasks(tasksByStatus);
    
    // Función para cargar etiquetas desde DynamoDB o usar las locales como fallback
    const loadTags = async () => {
      try {
        const tagsFromDB = await loadWorkspaceTagsFromDynamoDB(activeWorkspace);
        
        if (tagsFromDB.length > 0) {
          // Usar etiquetas de DynamoDB si están disponibles (siempre como strings válidas)
          setAvailableTags(normalizeTagList(tagsFromDB));
        } else {
          // Fallback: Obtener etiquetas dinámicamente desde las tarjetas del workspace activo
          const uniqueTags = getUniqueTags(activeWorkspace);
          setAvailableTags(normalizeTagList(uniqueTags));
        }
      } catch (error) {
        console.error('Error al cargar etiquetas desde DynamoDB:', error);
        // Fallback: Obtener etiquetas dinámicamente desde las tarjetas del workspace activo
        const uniqueTags = getUniqueTags(activeWorkspace);
        setAvailableTags(normalizeTagList(uniqueTags));
      }
    };
    
    loadTags();
    
    // Obtener títulos únicos de tareas - excluyendo las que tienen status "deleted"
    const uniqueTitles = Array.from(new Set(
      workspaces[activeWorkspace]?.tasks
        .filter(task => task.status !== 'deleted') // Excluir tareas eliminadas
        .map(task => task.title)
        .filter(title => title && title.trim() !== '')
    ));
    setAvailableTitles(uniqueTitles);
    
    // Sincronizar el searchQuery con el filtro activo
    const currentFilter = workspaces[activeWorkspace]?.activeFilter || '';
    setSearchQuery(currentFilter);
    
    // Sincronizar el titleQuery con el filtro de título activo
    const currentTitleFilterValue = workspaces[activeWorkspace]?.activeTitleFilter || '';
    setTitleQuery(currentTitleFilterValue);
    
  }, [workspaces, activeWorkspace, getUniqueTags, loadWorkspaceTagsFromDynamoDB]);

  // Effect para cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
      
      if (
        titleSuggestionsRef.current && 
        !titleSuggestionsRef.current.contains(event.target) &&
        titleInputRef.current && 
        !titleInputRef.current.contains(event.target)
      ) {
        setShowTitleSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filtrar sugerencias basadas en la búsqueda de títulos
  useEffect(() => {
    if (titleQuery) {
      const filtered = availableTitles.filter(title => 
        title.toLowerCase().includes(titleQuery.toLowerCase())
      );
      setFilteredTitleSuggestions(filtered);
    } else {
      setFilteredTitleSuggestions(availableTitles);
    }
  }, [titleQuery, availableTitles]);

  // Filtrar sugerencias basadas en la búsqueda de etiquetas
  useEffect(() => {
    const safeList = availableTags.filter((tag) => safeTagString(tag));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const filtered = safeList.filter((tag) =>
        safeTagString(tag).toLowerCase().includes(q)
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(safeList);
    }
  }, [searchQuery, availableTags]);

  // Manejar el botón de retroceso del navegador para modales
  useEffect(() => {
    // Solo agregar un estado al historial cuando se abre un modal
    if (showTaskForm || showConfirmation) {
      // Agregar una entrada al historial cuando se abre un modal
      window.history.pushState({ modal: true }, "");
      
      // Manejador para el evento popstate (botón atrás)
      const handlePopState = (event) => {
        // Cerrar modales en orden de prioridad
        if (showConfirmation) {
          setShowConfirmation(false);
          setTaskToConfirm(null);
        } else if (showTaskForm) {
          setShowTaskForm(false);
        }
        
        // Prevenir que el navegador vaya hacia atrás realmente
        if (event.state !== null) {
          window.history.pushState({ modal: true }, "");
        }
      };
      
      // Agregar el manejador de eventos
      window.addEventListener('popstate', handlePopState);
      
      // Limpiar el manejador al desmontar
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showTaskForm, showConfirmation, setShowTaskForm]);

  // Cerrar formulario y modales con la tecla Escape
  useEscapeKey(() => {
    if (showTaskForm) {
      setShowTaskForm(false);
    } else if (showConfirmation) {
      setShowConfirmation(false);
      setTaskToConfirm(null);
    }
  }, showTaskForm || showConfirmation);

  const moveTask = (taskId, sourceStatus, targetStatus, updatedTaskData) => {
    if (!canUserDoOnBoard('moveBetweenStates')) return;
    const task = workspaces[activeWorkspace]?.tasks.find(t => t.id === taskId);
    if (!task) return;

    // Verificar si estamos moviendo a "done" para mostrar confirmación
    if (targetStatus === 'done') {
      setTaskToConfirm({
        taskId,
        sourceStatus,
        targetStatus,
        updatedTaskData
      });
      setShowConfirmation(true);
      setEvidenceComment(''); // Limpiar el comentario anterior
      return; // Detenemos la ejecución hasta que se confirme
    }

    // Verificar si la tarea ya tiene un responsable
    const hasAssignee = task.assignee && task.assignee.trim() !== '';

    // Si movemos a inProgress y no tiene responsable, asignar al usuario actual
    if (targetStatus === 'inProgress' && !hasAssignee) {
      const newUpdatedData = {
        ...(updatedTaskData || {}),
        assignee: currentUser ? currentUser.email : '',
        status: targetStatus
      };
      updateTask(activeWorkspace, taskId, newUpdatedData);
    }
    // Si se mueve a "done", asegurarnos de incluir la fecha de finalización
    else if (targetStatus === 'done') {
      // Usar generateLocalTimestamp para consistencia
      const formattedCompletionDate = generateLocalTimestamp();
      
      // Si recibimos datos adicionales para la tarea, usamos updateTask
      const newData = {
        ...(updatedTaskData || {}),
        status: targetStatus,
        completedDate: formattedCompletionDate
      };
      
      updateTask(activeWorkspace, taskId, newData);
    }
    // Si ya tiene asignado o va a otro estado, proceder normal
    else if (updatedTaskData) {
      // Si recibimos datos adicionales para la tarea, usamos updateTask
      const newData = {
        ...updatedTaskData,
        status: targetStatus
      };
      updateTask(activeWorkspace, taskId, newData);
    } else {
      // Si solo cambia el estado, usamos updateTaskStatus
      updateTaskStatus(activeWorkspace, taskId, targetStatus);
    }
  };

  const handleAddTask = (newTask) => {
    // Usar onAddTask del componente padre si está disponible, o el addTask del contexto
    if (onAddTask) {
      onAddTask(newTask);
    } else {
      addTask(activeWorkspace, newTask);
      if (setShowTaskForm) {
        setShowTaskForm(false);
      }
    }
  };

  const handleDeleteTask = (taskId) => {
    // Eliminar el window.confirm y dejar que nuestro modal personalizado maneje la confirmación
    deleteTask(activeWorkspace, taskId);
  };
  
  // Manejador para cambiar el filtro de etiquetas
  const handleTagFilterChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setShowSuggestions(true);
    
    // Si el campo está vacío, limpiar el filtro
    if (newQuery === '') {
      updateWorkspaceFilter(activeWorkspace, "");
    }
  };
  
  // Manejador para seleccionar una sugerencia
  const handleSelectSuggestion = (tag) => {
    const normalized = normalizeTagList([tag])[0];
    if (!normalized) return;
    setSearchQuery(normalized);
    updateWorkspaceFilter(activeWorkspace, normalized);
    setShowSuggestions(false);
  };
  
  // Manejador para limpiar el filtro
  const clearTagFilter = () => {
    setSearchQuery('');
    updateWorkspaceFilter(activeWorkspace, "");
  };
  
  // Manejador para cambiar el filtro de títulos
  const handleTitleFilterChange = (e) => {
    const newQuery = e.target.value;
    setTitleQuery(newQuery);
    setShowTitleSuggestions(true);
    
    // Si el campo está vacío, limpiar el filtro
    if (newQuery === '') {
      updateTitleFilter(activeWorkspace, "");
    }
  };
  
  // Manejador para seleccionar una sugerencia de título
  const handleSelectTitleSuggestion = (title) => {
    setTitleQuery(title);
    updateTitleFilter(activeWorkspace, title);
    setShowTitleSuggestions(false);
  };
  
  // Manejador para limpiar el filtro de título
  const clearTitleFilter = () => {
    setTitleQuery('');
    updateTitleFilter(activeWorkspace, "");
  };

  const confirmCompleteTask = () => {
    if (!taskToConfirm) return;
    if (!canUserDoOnBoard('moveBetweenStates')) return;
    
    const { taskId, targetStatus, updatedTaskData } = taskToConfirm;
    
    // Preparar los datos de actualización
    let finalUpdatedData = updatedTaskData || {};
    
    // Si hay un comentario de evidencia, agregarlo
    if (evidenceComment.trim()) {
      const task = workspaces[activeWorkspace]?.tasks.find(t => t.id === taskId);
      if (task) {
        const timestamp = new Date().toISOString();
        const userEmail = currentUser?.email || 'Usuario';
        
        // Crear el nuevo comentario de evidencia
        const evidenceCommentObj = {
          id: `evidence-${Date.now()}`,
          text: evidenceComment.trim(),
          timestamp: timestamp,
          author: userEmail,
          isEvidence: true // Marcar como comentario de evidencia
        };
        
        // Agregar el comentario a los comentarios existentes
        const updatedComments = [...(task.comments || []), evidenceCommentObj];
        
        // Crear entrada en el historial de actividad para el comentario de evidencia
        const commentActivityEntry = {
          timestamp: timestamp,
          type: "EVIDENCE_COMMENT_ADDED",
          field: "comments",
          oldValue: null,
          newValue: evidenceComment.trim(),
          updatedBy: userEmail
        };
        
        // Agregar al historial de actividad existente
        const existingActivityHistory = finalUpdatedData.activityHistory || task.activityHistory || [];
        const updatedActivityHistory = [...existingActivityHistory, commentActivityEntry];
        
        // Actualizar los datos finales
        finalUpdatedData = {
          ...finalUpdatedData,
          comments: updatedComments,
          activityHistory: updatedActivityHistory
        };
      }
    }
    
    // IMPORTANTE: Si se está moviendo a "done", asegurarse de que siempre se establezca completedDate
    if (targetStatus === 'done') {
      // Si no existe completedDate en los datos o está vacío, establecerlo
      if (!finalUpdatedData.completedDate || finalUpdatedData.completedDate.trim() === '') {
        const formattedCompletionDate = generateLocalTimestamp();
        finalUpdatedData.completedDate = formattedCompletionDate;
      }
    }
    
    if (finalUpdatedData && Object.keys(finalUpdatedData).length > 0) {
      const newData = {
        ...finalUpdatedData,
        status: targetStatus
      };
      updateTask(activeWorkspace, taskId, newData);
    } else {
      updateTaskStatus(activeWorkspace, taskId, targetStatus);
    }
    
    // Limpiar estado
    setShowConfirmation(false);
    setTaskToConfirm(null);
    setEvidenceComment('');
  };
  
  // Función para cancelar la finalización
  const cancelCompleteTask = () => {
    setShowConfirmation(false);
    setTaskToConfirm(null);
    setEvidenceComment('');
  };

  const renderColumn = (title, status, tasks) => {
    return (
      <div 
        className={`kanban-column ${status === 'inProgress' && tasks.length > 0 ? 'over' : ''}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          if (!canUserDoOnBoard('moveBetweenStates')) return;
          const taskId = e.dataTransfer.getData('taskId');
          const sourceStatus = e.dataTransfer.getData('sourceStatus');
          
          // Validar reglas de negocio:
          // 1. Solo se puede mover a inProgress o done si tiene un responsable asignado
          const task = workspaces[activeWorkspace]?.tasks.find(t => t.id === taskId);
          
          if (!task) return;

          // Verificar si la tarea ya tiene un responsable
          const hasAssignee = task.assignee && task.assignee.trim() !== '';
          
          // Si es pendiente, se puede mover a cualquier sitio
          if (sourceStatus === 'todo') {
            if ((status === 'inProgress' || status === 'done') && !hasAssignee) {
              // Si va a inProgress o done y no tiene responsable, asignar al usuario actual
              const timestamp = new Date().toISOString();
              const userEmail = currentUser ? currentUser.email : '';
              
              // Crear entradas para el historial de actividad
              const assigneeActivityEntry = {
                timestamp: timestamp,
                type: "ASSIGNEE_CHANGE",
                field: "assignee",
                oldValue: null,
                newValue: userEmail,
                updatedBy: userEmail
              };
              
              const activityHistory = [
                ...(task.activityHistory || []),
                assigneeActivityEntry
              ];
              
              const updatedTask = {
                ...task,
                assignee: userEmail,
                activityHistory: activityHistory,
                updatedAt: timestamp
              };
              moveTask(taskId, sourceStatus, status, updatedTask);
            }
            else {
              // Si ya tiene responsable o va a otro estado, solo cambiar estado
              moveTask(taskId, sourceStatus, status);
            }
          }
          // Si es inProgress o done, solo cambiar estado
          else {
            moveTask(taskId, sourceStatus, status);
          }
        }}
      >
        <div className="column-header">
          <h3>{title}</h3>
          <span className="task-count">{tasks.length}</span>
        </div>
        <div className="tasks-container">
          {tasks.map(task => (
            <TaskCard 
              key={`${task.id}-${task.updatedAt || ''}`}
              task={task} 
              onMoveTask={moveTask}
              onDeleteTask={handleDeleteTask}
              onUpdateTask={(updatedTask) => {
                // Si la tarea se actualiza directamente en el componente,
                // esta función permite reflejar los cambios en la UI sin esperar
                // a que los datos se recarguen del backend
                const updatedTasks = [...tasks];
                const taskIndex = updatedTasks.findIndex(t => t.id === updatedTask.id);
                if (taskIndex !== -1) {
                  // Actualizar la tarea en la lista local
                  updatedTasks[taskIndex] = updatedTask;
                  
                  // Forzar la actualización visual usando Object.assign para modificar directamente la referencia
                  // (Esto es un hack pero evita tener que usar setWorkspaces)
                  Object.assign(tasks[taskIndex], updatedTask);
                  
                  // Si estamos dentro de un modal y la tarea se actualiza,
                  // no necesitamos llamar a updateTask de nuevo, ya que ya fue llamado
                  // desde el componente TaskCard al subir el archivo
                }
              }}
            />
          ))}
          
          {tasks.length === 0 && (
            <div className="empty-column">
              <p>No hay tareas</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Si no hay workspace activo o está cargando, mostrar loader o mensaje
  if (!workspaces[activeWorkspace]) {
    return <div className="loading-container">Cargando tablero...</div>;
  }

  const currentFilter = workspaces[activeWorkspace].activeFilter || '';
  const currentTitleFilter = workspaces[activeWorkspace].activeTitleFilter || '';

  return (
    <div className="kanban-container">
      <div className="filters-container">
        {/* Filtro de título con dropdown y autocompletado */}
        <div className="tag-filter-container">
          <div className="filter-controls">
            <div className="autocomplete-container">
              <input
                type="text"
                className="tag-filter-input"
                placeholder="Buscar por título..."
                value={titleQuery}
                onChange={handleTitleFilterChange}
                onFocus={() => setShowTitleSuggestions(true)}
                ref={titleInputRef}
              />
              
              {showTitleSuggestions && (
                <>
                  <div className="autocomplete-suggestions" ref={titleSuggestionsRef}>
                    {filteredTitleSuggestions.length > 0 ? (
                      filteredTitleSuggestions.map((title, index) => (
                        <div 
                          key={index} 
                          className="suggestion-item"
                          onClick={() => handleSelectTitleSuggestion(title)}
                        >
                          {title}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No se encontraron coincidencias
                      </div>
                    )}
                  </div>
                  <div className="mobile-overlay" onClick={() => setShowTitleSuggestions(false)}></div>
                </>
              )}
            </div>
            
            {/* Botón X con nuevo estilo */}
            {titleQuery && (
              <button 
                className="clear-filter-btn" 
                onClick={clearTitleFilter}
                title="Limpiar filtro"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Filtro de etiquetas con dropdown y autocompletado */}
        <div className="tag-filter-container">
          <div className="filter-controls">
            <div className="autocomplete-container">
              <input
                type="text"
                className="tag-filter-input"
                placeholder="Buscar etiquetas..."
                value={searchQuery}
                onChange={handleTagFilterChange}
                onFocus={() => setShowSuggestions(true)}
                ref={searchInputRef}
              />
              
              {showSuggestions && (
                <>
                  <div className="autocomplete-suggestions" ref={suggestionsRef}>
                    {filteredSuggestions.length > 0 ? (
                      filteredSuggestions.map((tag, index) => (
                        <div 
                          key={index} 
                          className="suggestion-item"
                          onClick={() => handleSelectSuggestion(tag)}
                        >
                          {tag}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-results">
                        No se encontraron coincidencias
                      </div>
                    )}
                  </div>
                  <div className="mobile-overlay" onClick={() => setShowSuggestions(false)}></div>
                </>
              )}
            </div>
            
            {/* Botón X con nuevo estilo */}
            {searchQuery && (
              <button 
                className="clear-filter-btn" 
                onClick={clearTagFilter}
                title="Limpiar filtro"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Filtro de responsable */}
        <AssigneeFilter />
      </div>
      
      {/* Mensaje cuando no hay tareas que coincidan con los filtros */}
      {(organizedTasks.todo.length === 0 && 
        organizedTasks.inProgress.length === 0 && 
        organizedTasks.done.length === 0 && 
        (currentFilter || workspaces[activeWorkspace].activeAssigneeFilter || currentTitleFilter)) && (
        <div className="no-tasks-message">
          <p>No hay tareas que coincidan con los filtros seleccionados.</p>
        </div>
      )}
      
      <DndProvider backend={HTML5Backend}>
        <div className="kanban-columns">
          {renderColumn('Pendiente', 'todo', organizedTasks.todo)}
          {renderColumn('En progreso', 'inProgress', organizedTasks.inProgress)}
          {renderColumn('Completado', 'done', organizedTasks.done)}
        </div>
      </DndProvider>
      
      {/* Modal para crear tarea si no viene de Home */}
      {showTaskForm && !onAddTask && (
        <div className="task-form-overlay" onClick={() => setShowTaskForm(false)}>
          <div className="task-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-form-header">
              <h3>Crear Nueva Tarea</h3>
              <button 
                className="close-btn"
                onClick={() => setShowTaskForm(false)}
              >
                ✕
              </button>
            </div>
            <TaskForm 
              onComplete={() => setShowTaskForm(false)} 
              onAddTask={handleAddTask}
              initialWorkspace={activeWorkspace}
            />
          </div>
        </div>
      )}
      
      {/* Modal de confirmación para completar tarea */}
      {showConfirmation && taskToConfirm && (
        <div className="task-form-overlay">
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <h3>Confirmar acción</h3>
            </div>
            <div className="confirmation-content">
              <p>¿Estás seguro de que deseas marcar esta tarea como completada?</p>
              <p>Esta acción indicará que todos los entregables han sido finalizados.</p>
              
              <div className="evidence-section">
                <h4>¿Deseas dejar evidencia?</h4>
                <textarea
                  className="evidence-textarea"
                  placeholder="Describe la evidencia de finalización (opcional)..."
                  value={evidenceComment}
                  onChange={(e) => setEvidenceComment(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="confirmation-actions">
              <button 
                className="cancel-btn"
                onClick={cancelCompleteTask}
              >
                Cancelar
              </button>
              <button 
                className="confirm-btn"
                onClick={confirmCompleteTask}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Botón flotante de historial de actividades */}
      <button 
        className="floating-history-btn"
        onClick={() => setShowActivityHistory(true)}
        title="Ver historial de actividades (tareas eliminadas y archivadas)"
      >
        📋
      </button>
      
      {/* Modal de historial de actividades */}
      <ActivityHistory 
        isOpen={showActivityHistory}
        onClose={() => setShowActivityHistory(false)}
      />
    </div>
  );
}

export default KanbanBoard; 